import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { siteStats, blacklistEntries } from "@/mocks/blacklist";

const HOT_KEYWORDS = blacklistEntries.slice(0, 3).map((entry) => ({
  name: entry.name,
  href: `/blacklist/${entry.id}`,
}));

export default function Hero() {
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/blacklist?q=${encodeURIComponent(value)}` : "/blacklist");
  };

  return (
    <section className="relative overflow-hidden border-b border-background-200 bg-gradient-to-b from-background-100 via-background-50 to-background-50">
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 left-1/4 h-72 w-72 rounded-full bg-accent-100/60 blur-3xl" />

      <div className="relative mx-auto grid max-w-content grid-cols-1 items-center gap-12 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
        <div className="w-full animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3.5 py-1.5 font-label text-xs font-medium text-primary-800">
            <i className="ri-radar-line text-sm" />
            付款前先查一查 · 少踩一次坑
          </span>

          <h1 className="mt-5">
            <span className="block font-label text-sm font-medium tracking-wide text-primary-700">
              避雷哨 · 网站黑料风险记录库
            </span>
            <span className="mt-3 block font-heading text-3xl font-black leading-tight text-foreground-950 sm:text-4xl lg:text-[2.6rem]">
              在踩坑之前，
              <br className="hidden sm:block" />
              先查一查网站黑料
            </span>
          </h1>

          <p className="mt-5 max-w-xl font-body text-base leading-relaxed text-foreground-600">
            收录避雷名单、不靠谱平台、诈骗举报、跑路记录与投诉汇总。输入平台名称，看看是否已有同类反馈，再决定要不要付款。
          </p>

          <form onSubmit={handleSubmit} role="search" className="mt-7 flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 transition-colors focus-within:border-primary-400">
              <i className="ri-search-line text-lg text-foreground-500" />
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入平台名称或域名"
                aria-label="输入平台名称或域名"
                className="w-full bg-transparent font-label text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-file-search-line text-base" />
              查询风险记录
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="font-label text-xs text-foreground-500">热门查询</span>
            {HOT_KEYWORDS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1 font-label text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
              >
                {item.name}
              </Link>
            ))}
          </div>

          <dl className="mt-9 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-background-200 pt-6 sm:grid-cols-4">
            {siteStats.map((stat) => (
              <div key={stat.label}>
                <dt className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{stat.value}</dt>
                <dd className="mt-1 font-label text-xs text-foreground-500">{stat.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <div className="relative overflow-hidden rounded-xl border border-background-200 bg-background-100">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20editorial%20illustration%20of%20a%20risk%20monitoring%20radar%20interface%2C%20concentric%20rings%20and%20scattered%20warning%20dots%20over%20layered%20translucent%20panels%2C%20warm%20amber%20and%20deep%20charcoal%20palette%20with%20muted%20teal%20accents%2C%20minimal%20flat%20geometric%20design%2C%20soft%20gradient%20background%2C%20no%20text%2C%20no%20letters&width=1000&height=1200&seq=home-hero-radar-2026&orientation=portrait"
              alt="网站风险监测雷达示意图"
              title="网站黑料风险查询 - 避雷哨"
              className="h-[360px] w-full object-cover md:h-[460px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/45 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 rounded-md bg-background-50/95 px-4 py-3">
              <p className="font-label text-xs text-foreground-500">今日高危提示</p>
              <p className="mt-1 font-heading text-sm font-semibold text-foreground-950">
                高息保本、垫付兼职、超低价商城，先查再付款
              </p>
            </div>
          </div>

          <div className="absolute -left-3 bottom-24 w-48 rounded-lg border border-background-200 bg-background-50 p-4 sm:-left-6">
            <p className="font-label text-xs text-foreground-500">本周新增线索</p>
            <p className="mt-1 font-heading text-2xl font-bold text-accent-600">+126</p>
            <p className="mt-1 font-label text-xs text-foreground-500">其中高危 38 条</p>
          </div>

          <div className="absolute -right-2 top-6 flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3.5 py-2 sm:-right-4">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-secondary-400" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-secondary-500" />
            </span>
            <span className="font-label text-xs font-medium text-foreground-800">实时收录中</span>
          </div>
        </div>
      </div>
    </section>
  );
}